import React from 'react';
import Navbar from '@/components/Navbar';
import { Lock, ChatCircle, ShieldCheck, Star, MapPin, Scales, Prohibit, CalendarX, Checks } from '@phosphor-icons/react';

const TRUST_ITEMS = [
  { icon: <Lock size={22} />, title: 'Secure escrow payments', desc: 'Your payment is held safely until the service is completed. Full refund if anything goes wrong.' },
  { icon: <ChatCircle size={22} />, title: 'In-app messaging only', desc: 'Phone numbers and emails are automatically blocked before booking. Contact info only unlocked after confirmed payment.' },
  { icon: <ShieldCheck size={22} />, title: 'Bookr protection', desc: 'If a provider cancels last-minute, we find a replacement or issue a full refund — guaranteed.' },
  { icon: <Checks size={22} />, title: 'Verified providers', desc: 'Every provider is ID-verified with a reviewed portfolio. Top Bookr badges go only to the best performers.' },
  { icon: <Star size={22} weight="fill" />, title: 'Real reviews only', desc: 'Reviews are only possible after a completed booking. No fake ratings — ever.' },
  { icon: <MapPin size={22} />, title: 'Local-first', desc: 'Providers set their exact service radius. You only see providers who can actually reach you.' },
  { icon: <Scales size={22} />, title: 'Dispute resolution', desc: 'Our team reviews all disputes within 24 hours. Clear cancellation policies protect both sides.' },
  { icon: <Prohibit size={22} />, title: 'Anti-off-platform system', desc: 'Providers who attempt to move bookings off-platform are flagged, ranked lower, or suspended.' },
  { icon: <CalendarX size={22} />, title: 'Clear cancellation policies', desc: 'Every provider shows their policy — Flexible, Moderate, or Strict — before you book. If a provider cancels, you get 100% back, always.' },
];

const GUARANTEE_POINTS = [
  'Your money sits in escrow — providers are only paid after the work is done',
  'Provider cancels or doesn’t show? Full refund, automatically, no questions asked',
  'Something went wrong? Report it within 48 hours and our team reviews it — payout is paused until it’s resolved',
  'Every provider is reviewed and approved by Bookr before they can take a booking',
];

export default function TrustPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="trust-page">
      <Navbar />
      <div className="bg-hero-wash">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
          <div className="max-w-2xl mx-auto text-center fade-up">
            <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-5 text-primary">
              <ShieldCheck size={26} weight="fill" />
            </div>
            <p className="text-sm font-medium tracking-widest uppercase text-primary mb-3">Trust &amp; Safety</p>
            <h1 className="text-4xl md:text-5xl font-display font-semibold tracking-tight leading-[1.1] text-foreground mb-4">
              Your booking is protected
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed max-w-lg mx-auto">
              Every layer of Bookr — from payment to reviews — is built so you can book with complete confidence.
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        {/* The Bookr Guarantee */}
        <div className="bg-primary rounded-3xl p-8 md:p-12 mb-12 text-primary-foreground shadow-lift fade-up" data-testid="bookr-guarantee">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={28} weight="fill" />
            <h2 className="text-2xl md:text-3xl font-display font-semibold tracking-tight">The Bookr Guarantee</h2>
          </div>
          <p className="text-primary-foreground/80 text-sm md:text-base leading-relaxed max-w-2xl mb-6">
            Book any provider on Bookr.ai knowing the worst-case scenario is a full refund. Here's exactly what we stand behind:
          </p>
          <ul className="grid md:grid-cols-2 gap-3">
            {GUARANTEE_POINTS.map((p, i) => (
              <li key={i} className="flex items-start gap-2.5 bg-primary-foreground/10 rounded-xl px-4 py-3 text-sm leading-relaxed">
                <Checks size={16} weight="bold" className="shrink-0 mt-0.5" /> {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TRUST_ITEMS.map((t, i) => (
            <div
              key={i}
              className="bg-card rounded-2xl border border-border/70 shadow-soft card-lift p-6 fade-up"
              style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}
              data-testid={`trust-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center text-primary mb-4">
                {t.icon}
              </div>
              <h3 className="text-lg font-display font-semibold tracking-tight text-foreground mb-2">{t.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
